// Composer that builds a single pdfmake doc-def containing any combination
// of Cost Report + Variance Analysis. Used by BudgetOverview's
// SharePdfButton renderPdfMakeDoc — returns null when the user hasn't
// selected either section (the SharePdfButton then falls back to the
// html2pdf path, though in practice the legacy path is unused now that
// both sections are pdfmake-backed).
//
// Page-break behaviour: when both sections are included, the Variance
// Analysis content starts on a fresh page. pdfmake honours `pageBreak:
// 'before'` on the first element of the second section. If only one
// section is present, no break is inserted.

import { buildBudgetReportContent } from './budgetReportPdf';
import { buildVarianceAnalysisContent } from './variancePdf';

const FOOTER_GREY = '#999999';

const dateShort = () => new Date().toLocaleDateString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric',
});

export function buildBudgetVariancePdf({
  includeOverview,
  includeVariance,
  includeUnchanged,
  // Shared
  reportData,
  departmentName,
  productionName,
  companyName,
  includeVat,
  formatMoney,
  // Cost Report
  versionName,
  // Optional: older-version ids the Cost Report section prints (the Overview's
  // Compare picker). Passed to the Cost Report ONLY — the Variance section
  // keeps its own baseline and never reads this.
  compareVersionIds,
  // Baseline version name. Used by the Variance section below; passed through
  // to buildBudgetReportContent for API compatibility but no longer rendered
  // on the Cost Report header (that page is spend vs current budget only).
  baselineName,
  // Variance
  compareData,
  currentVersionName,
  comparisonVersionName,
  baselineVersionId,
  // Anchor-column wording. Default "Baseline" is correct for the Overview
  // PDF (variance is anchored to the actual oldest version). The Compare
  // pop-out PDF passes "Selected" because the user picks the comparison
  // target — it isn't necessarily the original baseline.
  baselineLabel,
  // Optional doc-level identity. When provided, render a centred title
  // strip on page 1 so the reader can tell at a glance whether they're
  // looking at the stakeholder Status Report or an ad-hoc Version
  // Comparison. Also overrides the PDF metadata title/subject.
  // Omitted → fall back to the existing derived "<Dept> Cost Report …"
  // string for backward compatibility.
  pdfTitle,
  pdfSubtitle,
}) {
  if (!includeOverview && !includeVariance) return null;

  const content = [];

  if (includeOverview) {
    const overviewContent = buildBudgetReportContent({
      reportData,
      departmentName,
      productionName,
      companyName,
      versionName,
      baselineName,
      formatMoney,
      compareVersionIds,
    });
    content.push(...overviewContent);
  }

  if (includeVariance) {
    if (!compareData) {
      // Variance was checked but no comparison data is available — bail
      // gracefully rather than producing an empty variance section.
      if (content.length === 0) return null;
    } else {
      const varianceContent = buildVarianceAnalysisContent({
        compareData,
        reportData,
        currentVersionName,
        comparisonVersionName,
        baselineVersionId,
        baselineLabel,
        departmentName,
        productionName,
        companyName,
        includeVat,
        includeUnchanged,
        formatMoney,
      });
      // When the Overview is also present, force the Variance Analysis to
      // start on a fresh page. pdfmake applies pageBreak to the first
      // content element of the slice.
      if (content.length > 0 && varianceContent.length > 0) {
        varianceContent[0] = { ...varianceContent[0], pageBreak: 'before' };
      }
      content.push(...varianceContent);
    }
  }

  // Document title in the file metadata reflects what's inside. When the
  // caller supplies an explicit pdfTitle, it overrides the derived form
  // (Overview PDF passes "Budget Status Report", Compare pop-out passes
  // "Version Comparison Report" — see the two pages).
  const derivedDocTitle = includeOverview && includeVariance
    ? `${departmentName} Cost Report + Variance Analysis`
    : includeOverview
      ? `${departmentName} Cost Report`
      : `${departmentName} Variance Analysis`;
  const docTitle = pdfTitle || derivedDocTitle;

  const footerLabel = includeOverview && includeVariance
    ? 'Cost Report + Variance Analysis'
    : includeOverview
      ? 'Cost Report'
      : 'Variance Analysis';

  // Doc-level title strip. Rendered on page 1 above the first section so
  // the reader can tell on sight which report kind this is. Skipped when
  // the caller doesn't pass pdfTitle (back-compat for any older invocation).
  if (pdfTitle) {
    const titleStrip = {
      stack: [
        { text: pdfTitle, fontSize: 16, bold: true, color: '#1e3a5f', alignment: 'center' },
        ...(pdfSubtitle ? [{
          text: pdfSubtitle,
          fontSize: 10,
          italics: true,
          color: '#555',
          alignment: 'center',
          margin: [0, 2, 0, 0],
        }] : []),
      ],
      margin: [0, 0, 0, 10],
    };
    content.unshift(titleStrip);
  }

  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [28, 32, 28, 36],
    info: {
      title: docTitle,
      author: companyName || 'Crew Forge',
      subject: docTitle,
    },
    footer: (currentPage, pageCount) => ({
      columns: [
        {
          text: `Printed on ${dateShort()} | ${departmentName || ''} ${footerLabel} | Crew Forge`,
          fontSize: 7, color: FOOTER_GREY, alignment: 'left',
          margin: [28, 8, 0, 0],
        },
        {
          text: `Page ${currentPage} of ${pageCount}`,
          fontSize: 7, color: FOOTER_GREY, alignment: 'right',
          margin: [0, 8, 28, 0],
        },
      ],
    }),
    defaultStyle: { fontSize: 9, color: '#000' },
    content,
  };
}
